import { Avatar } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';

import {
  getEntityHeatBuzz, getSiteImageUrl, getSiteTitle, getSourceLetter, shareActionTitle,
  shareChangesState,
} from '../common/ItemTools';
import SelectionOverlay from './SelectionOverlay';
import VerdictBar, { useVerdictTransition } from './common/VerdictBar';
import {
  selectSelectionMode, selectSelectedIds, toggleSelection,
} from '../redux/features/context/contextSlice';
import { SERIF_STACK } from './common/tokens';
import HeatBuzzBadge from './common/HeatBuzzBadge';
import CardIdentity from './common/card/CardIdentity';
import useCardMenu from './common/card/useCardMenu';
import useCardDensity, { CardDensityProvider } from './common/card/useCardDensity';
import buildEntityMenuOptions from './common/card/entityMenu';
import { entityFacts, timeFacts, typeLabel } from './common/card/facts';
import CardFooter from './common/card/CardFooter';
import CardFavoriteAction from './common/card/CardFavoriteAction';
import CardShareAction from './common/card/CardShareAction';
import {
  ActionsBox, CompactActionButton, EntityCard, VerdictRow,
} from './common/card/styles';

const Logo = styled(Avatar)(({ theme }) => ({
  width: 'auto',
  height: '100%',
  aspectRatio: '1 / 1',
  maxWidth: '100%',
  borderRadius: '28%',
  fontFamily: SERIF_STACK,
  fontWeight: 'bold',
  fontSize: 'clamp(2rem, 8vw, 6rem)',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  backgroundColor: theme.palette.background.paper,
  '& .MuiAvatar-img': {
    objectFit: 'contain',
  },
}));

export default function SiteCard(props) {
  const {
    site,
    onSiteVerdict,
    onSiteEdit,
    onSiteDelete,
    onSiteUnshare,
    onSiteShare,
    onSiteFavorite,
    onSiteRead,
    onSiteMerge,
  } = props;

  const dispatch = useDispatch();
  const { cardRef, shows } = useCardDensity();
  const selectionMode = useSelector(selectSelectionMode);
  const selectedIds = useSelector(selectSelectedIds);
  const isSelected = selectedIds.includes(site.id);
  const onToggleSelect = () => dispatch(toggleSelection(site.id));

  const moderating = Boolean(onSiteVerdict);
  const showVerdictBar = moderating && !selectionMode;
  const handleDecide = React.useCallback(
    (kind, reason) => onSiteVerdict && onSiteVerdict(site, kind, reason),
    [onSiteVerdict, site],
  );
  const { decide, sx: verdictSx } = useVerdictTransition(handleDecide);

  const [, drag] = useDrag(() => ({
    type: 'site',
    item: { name: site.id },
    end: (dragItem, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dragItem && dropResult && onSiteMerge) {
        onSiteMerge(dropResult.name, [dragItem.name]);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }), [site.id, onSiteMerge]);

  const [, drop] = useDrop(() => ({
    accept: 'site',
    drop: () => ({ name: site.id }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [site.id]);

  // The root is both the drag source and what the ladder measures.
  const setCardNode = React.useCallback((node) => {
    cardRef.current = node;
    drag(node);
  }, [cardRef, drag]);

  const menuOptions = buildEntityMenuOptions({
    target: site,
    hasPrivateData: Boolean(site.hasPrivateData),
    hasPublicData: Boolean(site.hasPublicData),
    isFavorite: Boolean(site.isFavorite),
    shareDiff: site.shareDiff,
    moderating,
    collapsed: !shows('actions'),
    onEdit: onSiteEdit,
    onDelete: onSiteDelete,
    onUnshare: onSiteUnshare,
    onShare: onSiteShare,
    onFavorite: onSiteFavorite,
  });
  const { open: openMenu, menu } = useCardMenu(menuOptions);

  const stats = site.stats || {};
  const times = timeFacts(site);
  const facts = entityFacts({
    lead: typeLabel(site),
    age: times.published || times.created,
    stats,
    details: shows('factDetails'),
  });
  const { heatScore, buzzScore } = getEntityHeatBuzz(site);

  const canShare = onSiteShare && !moderating && site.hasPrivateData;
  const shareIsMarker = Boolean(site.hasPublicData)
    && shareChangesState(site.shareDiff) === 'none';

  const logoUrl = getSiteImageUrl(site);
  const sourceLetter = getSourceLetter(site);
  const siteTitle = getSiteTitle(site);

  return (
    <CardDensityProvider shows={shows}>
      <EntityCard raised ref={setCardNode} sx={moderating ? verdictSx : undefined}>
        <SelectionOverlay
          selectionMode={selectionMode}
          selected={isSelected}
          onToggle={onToggleSelect}
        />
        <CardIdentity
          onOpen={() => onSiteRead(site)}
          title={siteTitle}
          facts={facts}
          excerpt={shows('excerpt') ? site.excerpt : null}
          media={(
            <Logo src={logoUrl} ref={drop} variant="rounded">
              {sourceLetter || (siteTitle && siteTitle[0] && siteTitle[0].toUpperCase()) || '?'}
            </Logo>
          )}
          overlay={shows('heatBadge') ? <HeatBuzzBadge heat={heatScore} buzz={buzzScore} /> : null}
        />
        <CardFooter>
          <ActionsBox>
            {canShare && (shareIsMarker ? shows('markers') : shows('actions')) && (
              <CardShareAction
                hasPublicData={Boolean(site.hasPublicData)}
                changes={shareChangesState(site.shareDiff)}
                title={shareActionTitle(Boolean(site.hasPublicData), site.shareDiff)}
                onShare={() => onSiteShare(site)}
              />
            )}
            {(onSiteFavorite ? shows('actions') : shows('markers')) && (
              <CardFavoriteAction
                isFavorite={Boolean(site.isFavorite)}
                onToggle={onSiteFavorite && (() => onSiteFavorite(site))}
              />
            )}
            {menuOptions.length > 0 && !showVerdictBar && shows('menu') && (
              <CompactActionButton onClick={openMenu}>
                <MoreVertIcon />
              </CompactActionButton>
            )}
          </ActionsBox>
        </CardFooter>
        {showVerdictBar && (
          <VerdictRow>
            <VerdictBar onDecide={decide} menuOptions={menuOptions} />
          </VerdictRow>
        )}
        {menu}
      </EntityCard>
    </CardDensityProvider>
  );
}

SiteCard.propTypes = {
  onSiteVerdict: PropTypes.func,
  onSiteEdit: PropTypes.func,
  onSiteDelete: PropTypes.func,
  onSiteUnshare: PropTypes.func,
  onSiteShare: PropTypes.func,
  onSiteFavorite: PropTypes.func,
  onSiteRead: PropTypes.func,
  onSiteMerge: PropTypes.func,
};

SiteCard.defaultProps = {
  onSiteVerdict: null,
  onSiteEdit: null,
  onSiteDelete: null,
  onSiteUnshare: null,
  onSiteShare: null,
  onSiteFavorite: null,
  onSiteRead: null,
  onSiteMerge: null,
};
